import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { FileDown, ArrowRight } from 'lucide-react'
import { useRef, useEffect, useState, Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'

// Lazy load the 3D component to avoid SSR issues
const FGCable3D = lazy(() => import('@/components/FGCable3D').then(m => ({ default: m.FGCable3D })))

export function HeroSection() {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Mouse position for parallax effect
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  // Smooth spring animation for mouse following
  const springConfig = { damping: 30, stiffness: 100 }
  const smoothMouseX = useSpring(mouseX, springConfig)
  const smoothMouseY = useSpring(mouseY, springConfig)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // React doesn't always apply the muted attribute to the DOM correctly,
    // which causes browsers to block autoplay. Set it explicitly via ref.
    video.muted = true

    // If the video is already loaded (e.g. cached from a previous mount),
    // readyState >= 2 means enough data is available — mark as loaded immediately.
    if (video.readyState >= 2) {
      setIsVideoLoaded(true)
    }

    const tryPlay = () => {
      if (video.paused) {
        video.play().catch(() => {
          // Autoplay blocked, will show poster/fallback
        })
      }
    }

    tryPlay()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryPlay()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    mouseX.set(x)
    mouseY.set(y)
  }

  const handleMouseLeave = () => {
    mouseX.set(0)
    mouseY.set(0)
  }

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative min-h-screen overflow-hidden bg-[var(--color-void)]"
    >
      {/* Background Video */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        onLoadedData={() => setIsVideoLoaded(true)}
        onCanPlay={(e) => {
          const video = e.currentTarget
          if (video.paused) {
            video.play().catch(() => {})
          }
        }}
        poster="/media/images/hero-poster.jpg"
      >
        <source src="/media/videos/home-hero-video-final.mp4" type="video/mp4" />
      </video>

      {/* Fallback when video not loaded */}
      {!isVideoLoaded && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/media/images/hero-poster.jpg)' }}
        />
      )}

      {/* Cinematic Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-void)] via-[var(--color-void)]/90 to-[var(--color-void)]/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-void)] via-transparent to-[var(--color-void)]/40" />

      {/* Noise Texture */}
      <div className="noise-overlay" />

      {/* Gradient Mesh */}
      <div className="gradient-mesh" />

      {/* Animated Diagonal Lines */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-20">
        <motion.div
          className="absolute -left-1/2 top-1/4 h-[1px] w-[200%] bg-gradient-to-r from-transparent via-[var(--accent-primary)] to-transparent"
          style={{
            rotate: -15,
            x: useTransform(smoothMouseX, [-0.5, 0.5], [-20, 20]),
            y: useTransform(smoothMouseY, [-0.5, 0.5], [-10, 10]),
          }}
        />
        <motion.div
          className="absolute -left-1/2 top-1/2 h-[1px] w-[200%] bg-gradient-to-r from-transparent via-[var(--color-steel)] to-transparent"
          style={{
            rotate: -15,
            x: useTransform(smoothMouseX, [-0.5, 0.5], [20, -20]),
          }}
        />
      </div>

      {/* Marquee Background Text */}
      <div className="pointer-events-none absolute inset-0 flex items-center overflow-hidden">
        <motion.div
          className="whitespace-nowrap"
          animate={{ x: [0, '-50%'] }}
          transition={{
            duration: 40,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <span className="block select-none text-[18vw] font-black uppercase tracking-tighter text-white/5">
            {t('homepage.hero.scrollingText')}
          </span>
        </motion.div>
      </div>

      {/* Main Content */}
      <div
        className="relative z-10 flex min-h-screen items-center"
      >
        <div className="container">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
            {/* Left: Text content */}
            <div className="relative">
              {/* Year Badge */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="mb-8"
              >
                <div className="inline-flex items-center gap-4">
                  <span className="label">{t('company.since')}</span>
                  <div className="h-[1px] w-12 bg-[var(--color-graphite)]" />
                </div>
              </motion.div>

              {/* Main Title */}
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="headline-display"
              >
                {t('homepage.hero.headline')}
                <br />
                <em className="text-[var(--accent-primary)]">{t('homepage.hero.headlineAccent')}</em>
              </motion.h1>

              {/* Accent Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 h-[2px] w-24 origin-left bg-gradient-to-r from-[var(--accent-primary)] to-transparent"
              />

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="body-xl mt-8 max-w-lg"
              >
                {t('homepage.hero.description')}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <a
                  href="/media/documents/FG-PRESENTAZIONE-VERTICALE.pdf"
                  download
                  className="btn btn-primary group"
                >
                  <FileDown className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
                  {t('homepage.hero.downloadBrochure')}
                </a>
                <a href="#sectors" className="btn btn-secondary group">
                  {t('homepage.hero.ctaPrimary')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </motion.div>
            </div>

            {/* Right: Interactive 3D Cable */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block relative"
            >
              {/* Glow Effect Behind 3D */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-[400px] w-[400px] rounded-full bg-[var(--accent-primary)]/10 blur-[100px]" />
              </div>

              <div className="relative h-[600px] w-full">
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center">
                      <div className="h-32 w-32 animate-pulse rounded-full bg-[var(--accent-primary)]/20" />
                    </div>
                  }
                >
                  <FGCable3D
                    mouseX={smoothMouseX}
                    mouseY={smoothMouseY}
                  />
                </Suspense>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="label-caps text-[var(--color-ash)]">{t('homepage.hero.scrollIndicator')}</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex h-10 w-6 items-start justify-center rounded-full border border-[var(--color-graphite)] p-1"
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="h-2 w-1 rounded-full bg-[var(--accent-primary)]"
          />
        </motion.div>
      </motion.div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--color-void)] to-transparent" />
    </section>
  )
}
